/**
 * One tour, as a card inside its BU's section.
 *
 * Reach is the count of people who saw the CURRENT revision — bumping resets it, which is why the
 * line always says which revision it counts from. There is no total of eligible users: the BFF has
 * no census of a tenant's users, so no percentage is shown.
 *
 * @module Pages/BUs/Tours
 */

import { css } from '@emotion/css';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { Tour } from '../../Types/Tours.types';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const versionStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const pageStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
`;

const authorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const reachStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const reachCountStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const reachSinceStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
  margin-top: auto;
`;

const spacerStyle = css`
  flex: 1;
`;

export interface TourCardProps {
  tour: Tour;
  /** People who saw the current revision. `undefined` while the count is still loading. */
  viewCount?: number;
  /** Name of the tour's creator, when it could be resolved. */
  authorName?: string;
  canBump: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onBump: (tour: Tour) => void;
  onStats: (tour: Tour) => void;
  onEdit: (tour: Tour) => void;
  onDelete: (tour: Tour) => void;
}

export const TourCard = ({
  tour,
  viewCount,
  authorName,
  canBump,
  canEdit,
  canDelete,
  onBump,
  onStats,
  onEdit,
  onDelete,
}: TourCardProps): JSX.Element => (
  <article id={`tour-card-${tour.id}`} className={cardStyle}>
    <div className={headerRowStyle}>
      <StatusTag status={tour.isActive ? 'success' : 'neutral'} size='sm'>
        {tour.isActive ? 'Ativo' : 'Inativo'}
      </StatusTag>
      <span className={versionStyle}>v{tour.version}</span>
    </div>

    <div className={identityStyle}>
      <span className={pageStyle}>{tour.pageSlug}</span>
      {authorName ? <span className={authorStyle}>criado por {authorName}</span> : null}
    </div>

    <div className={reachStyle}>
      <span className={reachCountStyle}>
        {viewCount === undefined
          ? '—'
          : `${viewCount.toLocaleString('pt-BR')} ${viewCount === 1 ? 'usuário viu' : 'usuários viram'}`}
      </span>
      <span className={reachSinceStyle}>desde a revisão v{tour.version}</span>
    </div>

    <div className={footerStyle}>
      {canBump ? (
        <UTButton
          fitContent
          id={`tour-bump-${tour.id}`}
          variant='secondary'
          size='small'
          text='Subir revisão'
          icon='Arrows.ArrowsClockwise'
          onClick={() => {
            onBump(tour);
          }}
        />
      ) : null}

      <div className={spacerStyle} />

      <IconButton
        id={`tour-stats-${tour.id}`}
        icon='Misc.ChartLineUp'
        size='sm'
        aria-label={`Ver alcance do tour de ${tour.pageSlug}`}
        onClick={() => {
          onStats(tour);
        }}
      />
      {canEdit ? (
        <IconButton
          id={`tour-edit-${tour.id}`}
          icon='Settings.PencilSimple'
          size='sm'
          aria-label={`Editar tour de ${tour.pageSlug}`}
          onClick={() => {
            onEdit(tour);
          }}
        />
      ) : null}
      {canDelete ? (
        <IconButton
          id={`tour-delete-${tour.id}`}
          icon='Misc.Trash'
          size='sm'
          aria-label={`Excluir tour de ${tour.pageSlug}`}
          onClick={() => {
            onDelete(tour);
          }}
        />
      ) : null}
    </div>
  </article>
);
